// src/features/atividade/atividadeConfig.js
//
// O catálogo de modalidades da feature, e SÓ ele. Cada modalidade aponta para
// uma `activity_key` que JÁ EXISTE em src/features/treinos/utils/pointsConfig.js
// — nenhuma chave nova.
//
// POR QUE NÃO CRIAR CHAVES NOVAS. `pointsConfig` é a tabela de pontos do app
// inteiro: check-in manual, treino ao vivo, registro funcional, competição e
// clubes leem dela. Acrescentar uma linha ali muda a economia de pontos para
// todo mundo, não só para quem grava uma corrida. As sete modalidades do brief
// que têm par são mapeadas; `esqui`, que não tem, fica como pendência de
// produto em vez de virar stub na UI (ver RELATORIO-FASE0-ATIVIDADE §9).
//
// `velMaxMs` é o filtro de salto do geo.js — velocidade acima da qual o ponto é
// impossível para a modalidade e entra como erro de GPS, não como movimento.
// Os valores são folgados de propósito: recordes mundiais com margem, porque o
// filtro existe para pegar salto de 500 m em 1 s, não para julgar desempenho.

/** Modalidades gravadas por GPS, na ordem em que aparecem na lista completa. */
export const MODALIDADES = [
  {
    id: 'corrida',
    nome: 'Corrida',
    mede: 'Distância, ritmo e elevação',
    activityKey: 'corrida',
    icone: 'modality.corrida',
    velMaxMs: 12.5, // 45 km/h — recorde dos 100 m é ~10,4 m/s
    pausaMs: 0.5, // auto-pause abaixo de 0,5 m/s
    usaPace: true,
    usaPassos: true,
  },
  {
    id: 'caminhada',
    nome: 'Caminhada',
    mede: 'Distância, passos e elevação',
    activityKey: 'caminhada',
    icone: 'modality.caminhada',
    velMaxMs: 7,
    pausaMs: 0.5,
    usaPace: true,
    usaPassos: true,
  },
  {
    id: 'bike',
    nome: 'Bike',
    mede: 'Distância, velocidade e elevação',
    activityKey: 'ciclismo',
    icone: 'modality.bike',
    velMaxMs: 30, // 108 km/h — descida de serra
    pausaMs: 1, // bike para em semáforo com mais inércia
    usaPace: false, // ciclista lê km/h, não min/km
    usaPassos: false,
  },
  {
    id: 'trilha',
    nome: 'Trilha',
    mede: 'Distância, elevação e tempo',
    activityKey: 'trilha',
    icone: 'modality.trilha',
    velMaxMs: 8,
    pausaMs: 0.5,
    usaPace: true,
    usaPassos: true,
  },
  {
    id: 'natacao',
    nome: 'Natação',
    mede: 'Distância em águas abertas',
    activityKey: 'natacao',
    icone: 'modality.natacao',
    velMaxMs: 3,
    pausaMs: 0.3,
    usaPace: true,
    usaPassos: false,
  },
  {
    id: 'remo',
    nome: 'Remo',
    mede: 'Distância e ritmo na água',
    activityKey: 'remo',
    icone: 'modality.remo',
    velMaxMs: 8,
    pausaMs: 0.5,
    usaPace: true,
    usaPassos: false,
  },
  {
    id: 'patins',
    nome: 'Patins',
    mede: 'Distância e velocidade',
    activityKey: 'patinacao',
    icone: 'modality.patins',
    velMaxMs: 20,
    pausaMs: 1,
    usaPace: false,
    usaPassos: false,
  },
];

export const MODALIDADE_PADRAO = 'corrida';

const POR_ID = MODALIDADES.reduce((acc, m) => {
  acc[m.id] = m;
  return acc;
}, {});

/** Modalidade pelo id. Cai na padrão em vez de devolver null — a tela de
 *  gravação não tem estado "sem modalidade". */
export function modalidade(id) {
  return POR_ID[id] || POR_ID[MODALIDADE_PADRAO];
}

// ── Precisão e filtro ────────────────────────────────────────────────────────

/** Ponto com erro horizontal acima disto é descartado (brief §6.7). */
export const PRECISAO_MAX_M = 30;

/** Acima disto por 10 s seguidos, o banner de GPS fraco aparece (brief §5). */
export const PRECISAO_FRACA_M = 25;
export const ESPERA_BANNER_GPS_MS = 10000;

/** Janela da média móvel do pace, em metros (brief §6.7). */
export const JANELA_PACE_M = 200;

/** Segundos abaixo da velocidade mínima antes de auto-pausar (brief §5). */
export const AUTO_PAUSE_S = 6;

/** De quanto em quanto tempo o buffer local grava (brief §6.4). */
export const INTERVALO_PERSISTENCIA_MS = 5000;

/** Amostra do SAMPLES[] do documento salvo — uma a cada 5 s. */
export const INTERVALO_AMOSTRA_MS = 5000;

/** Duração do hold para parar, em ms (brief §6.1). */
export const HOLD_PARAR_MS = 1500;

/** Abaixo disto a atividade não vira registro: é toque sem querer. */
export const DURACAO_MINIMA_S = 30;
export const DISTANCIA_MINIMA_M = 50;

// ── Velocidade de veículo ────────────────────────────────────────────────────
//
// O TETO HUMANO SUSTENTADO de cada modalidade, em km/h. Passar disto por vários
// segundos seguidos não é treino — é carro, ônibus, moto, trem. A gravação
// pausa sozinha com motivo VEICULO (ver detectorVeiculo.js) e o trecho não
// conta distância.
//
// NÃO É O `velMaxMs` DO CATÁLOGO. Aquele é o filtro de SALTO do geo.js: pega o
// GPS que teleporta a pessoa 500 m em um segundo, e por isso é folgado
// (recorde mundial com margem). Este é o oposto: é baixo, porque a pergunta é
// "dá para manter isto por 15 s fazendo esta modalidade?", e é sustentado,
// porque pico de GPS de um segundo não pode pausar ninguém.
//
// Ficou num mapa à parte, e não como campo de cada modalidade, para que mudar
// um número aqui não mexa nas linhas do catálogo que as telas também editam.
//
// Os números, e por que não são mais apertados:
//   caminhada 12 — passo rápido é 6–7 km/h; 12 deixa trotar sem ser punido
//                  por isso, e qualquer veículo andando passa disto.
//   corrida   25 — 25 km/h sustentados é ritmo de 2'24"/km, acima do recorde
//                  mundial de 5 km. Tiro de 100 m passa, mas dura menos que a
//                  janela de confirmação.
//   trilha    20 — terreno irregular; trail run raramente passa de 15.
//   bike      65 — descida forte passa de 60; abaixo disso a bike perde para o
//                  carro na cidade, e o sensor de atividade do sistema (que
//                  separaria os dois) é pendência registrada no relatório.
//   natação   12 — nadador de elite faz ~7 km/h; acima é barco.
//   remo      30 — oito com timoneiro de elite faz ~22 km/h.
//   patins    50 — maratona de patins roda a ~35 km/h, descida passa de 40.
export const LIMITE_HUMANO_KMH = {
  caminhada: 12,
  corrida: 25,
  trilha: 20,
  bike: 65,
  natacao: 12,
  remo: 30,
  patins: 50,
};

/** Teto humano sustentado da modalidade, em m/s. `null` = sem detecção. */
export function limiteHumanoMs(modalidadeId) {
  const kmh = LIMITE_HUMANO_KMH[modalidadeId];
  return Number.isFinite(kmh) && kmh > 0 ? kmh / 3.6 : null;
}

/**
 * Segundos de "evidência" para confirmar o veículo. Não são 15 s corridos:
 * o detector acumula tempo acima do teto e DESCONTA devagar o tempo abaixo
 * (ver detectorVeiculo.js), para o carro parado no farol não zerar a conta.
 */
export const VEICULO_CONFIRMA_S = 15;

// ── Metas ────────────────────────────────────────────────────────────────────

/** Metas oferecidas no círculo "Meta" da pré-atividade. `valor` na unidade
 *  base: metros para distância, segundos para tempo, kcal para calorias. */
export const METAS = [
  { tipo: 'distancia', rotulo: '3 km', valor: 3000 },
  { tipo: 'distancia', rotulo: '5 km', valor: 5000 },
  { tipo: 'distancia', rotulo: '10 km', valor: 10000 },
  { tipo: 'distancia', rotulo: '21,1 km', valor: 21097 },
  { tipo: 'tempo', rotulo: '20 min', valor: 20 * 60 },
  { tipo: 'tempo', rotulo: '30 min', valor: 30 * 60 },
  { tipo: 'tempo', rotulo: '1 h', valor: 60 * 60 },
  { tipo: 'calorias', rotulo: '300 kcal', valor: 300 },
  { tipo: 'calorias', rotulo: '500 kcal', valor: 500 },
];

// ── Privacidade ──────────────────────────────────────────────────────────────

export const PRIVACIDADES = [
  { id: 'clube', rotulo: 'Meu clube' },
  { id: 'todos', rotulo: 'Todos' },
  { id: 'privado', rotulo: 'Só eu' },
];

export const PRIVACIDADE_PADRAO = 'clube';
